import PropTypes from 'prop-types';
import { Box, Stack } from '@mui/material';
import { SideNavItem } from './side-nav-item';

const defaultLeafActive = (pathname, path) => {
  if (!path) return false;
  return pathname === path || pathname.startsWith(`${path}/`);
};

const isLeafActive = (item, pathname) => {
  if (item.isActivePath) return item.isActivePath(pathname);
  return defaultLeafActive(pathname, item.path);
};

const branchHasActiveDescendant = (item, pathname) => {
  if (!item.items || !item.items.length) return false;
  return item.items.some((child) => {
    if (child.items) return branchHasActiveDescendant(child, pathname);
    return isLeafActive(child, pathname);
  });
};

const branchActive = (item, pathname) => {
  if (item.path && pathname.includes(item.path)) {
    return true;
  }
  return branchHasActiveDescendant(item, pathname);
};

const itemKey = (item, depth) => `${depth}-${item.path || item.title}-${item.key || ''}`;

const renderItems = ({ depth = 0, items, pathname, treeLineActive = false }) =>
  items.reduce(
    (acc, item) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
        treeLineActive,
      }),
    []
  );

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
  treeLineActive,
}) => {
  if (item.items) {
    const branchIsActive = branchActive(item, pathname);
    const lineActive = branchIsActive || treeLineActive;
    acc.push(
      <SideNavItem
        active={branchIsActive}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={itemKey(item, depth)}
        label={item.label}
        open={branchIsActive}
        path={item.path}
        title={item.title}
        treeLineActive={lineActive}
      >
        <Stack
          component="ul"
          spacing={0}
          sx={{
            gap: 'var(--nav-sub-list-gap)',
            listStyle: 'none',
            m: 0,
            ml: depth === 0 ? 'calc(var(--nav-item-px) + var(--nav-item-root-icon-box) / 2)' : 0,
            p: 0,
            pl: 'var(--nav-tree-indent)',
            position: 'relative',
            '&::before': {
              bgcolor: 'var(--nav-sub-tree-color)',
              bottom: 'calc(var(--nav-item-sub-min-height) / 2)',
              content: '""',
              left: 0,
              position: 'absolute',
              top: 0,
              transform: 'translateX(-0.5px)',
              width: '1px',
              zIndex: 0,
            },
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
            treeLineActive: lineActive,
          })}
        </Stack>
      </SideNavItem>
    );
  } else {
    acc.push(
      <SideNavItem
        active={isLeafActive(item, pathname)}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={itemKey(item, depth)}
        label={item.label}
        path={item.path}
        title={item.title}
        treeLineActive={treeLineActive}
      />
    );
  }

  return acc;
};

export const SideNavSection = (props) => {
  const { items = [], pathname, subheader = '', ...other } = props;

  return (
    <Stack
      component="ul"
      spacing={0}
      sx={{
        gap: 'var(--nav-list-gap)',
        listStyle: 'none',
        m: 0,
        p: 0,
      }}
      {...other}
    >
      {subheader && (
        <Box
          component="li"
          sx={{
            color: 'var(--nav-section-title-color)',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.66,
            mb: 1,
            ml: 1,
            textTransform: 'uppercase',
          }}
        >
          {subheader}
        </Box>
      )}
      {renderItems({ items, pathname })}
    </Stack>
  );
};

SideNavSection.propTypes = {
  items: PropTypes.array,
  pathname: PropTypes.string,
  subheader: PropTypes.string,
};
